import { User, UserRole } from '../types';
import apiClient, { emailApiClient } from './api';

export interface CreateUserDto {
  username: string;
  password: string;
  email: string;
  role?: UserRole;
  organ_name?: string;
  bubmyeng?: string;
}

export interface UpdateUserDto {
  username?: string;
  email?: string;
  role?: UserRole;
  organ_name?: string;
  bubmyeng?: string;
}

export interface UpdatePasswordDto {
  password: string;
}

export interface SearchUsersDto {
  keyword?: string;
  role?: UserRole;
  organ_name?: string;
  bubmyeng?: string;
}

export interface SendEmailDto {
  toEmails: string[];
  subject: string;
  content: string;
  fromName?: string;
}

export const userService = {
  // 모든 사용자 조회
  async getAllUsers(): Promise<User[]> {
    const response = await apiClient.get('/users');
    return response.data;
  },

  // 사용자 검색
  async searchUsers(searchParams: SearchUsersDto): Promise<User[]> {
    const response = await apiClient.get('/users', { params: searchParams });
    return response.data;
  },

  // 특정 사용자 조회
  async getUserById(userId: string): Promise<User> {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  },

  // 사용자 생성
  async createUser(userData: CreateUserDto): Promise<User> {
    const response = await apiClient.post('/users', userData);
    return response.data;
  },

  // 사용자 정보 수정
  async updateUser(userId: string, userData: UpdateUserDto): Promise<User> {
    const response = await apiClient.patch(`/users/${userId}`, userData);
    return response.data;
  },

  // 사용자 비밀번호 변경
  async updateUserPassword(userId: string, passwordData: UpdatePasswordDto): Promise<void> {
    await apiClient.patch(`/users/${userId}/password`, passwordData);
  },

  // 사용자 삭제
  async deleteUser(userId: string): Promise<void> {
    await apiClient.delete(`/users/${userId}`);
  },

  // 내 프로필 조회
  async getMyProfile(): Promise<User> {
    const response = await apiClient.get('/users/profile');
    return response.data;
  },

  // 내 프로필 수정
  async updateMyProfile(profileData: {
    email?: string;
    organ_name?: string;
    bubmyeng?: string;
  }): Promise<User> {
    const response = await apiClient.patch('/users/profile', profileData);
    return response.data;
  },

  // 내 비밀번호 변경
  async updateMyPassword(passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    await apiClient.patch('/users/profile/password', passwordData);
  },

  // 사용자 역할 업데이트 (임시 엔드포인트)
  async updateUserRole(username: string, role: UserRole): Promise<{
    success: boolean;
    message: string;
    user?: {
      user_id: string;
      username: string;
      email: string;
      role: UserRole;
    };
  }> {
    const response = await apiClient.post('/users/update-role', { username, role });
    return response.data;
  },

  // 이메일이 있는 사용자 목록 조회
  async getUsersWithEmail(): Promise<{
    success: boolean;
    data?: { user_id: string; username: string; email: string; role: string }[];
    message?: string;
  }> {
    const response = await apiClient.get('/users/with-email');
    return response.data;
  },

  // 메일 발송
  async sendBulkEmail(emailData: SendEmailDto): Promise<{
    success: boolean;
    message: string;
    result?: {
      success: string[];
      failed: string[];
    };
  }> {
    const response = await emailApiClient.post('/users/send-email', emailData);
    return response.data;
  },
}; 